import { AlertTriangle, Inbox, RefreshCw, ShieldAlert, SearchX } from 'lucide-react'
import type { ReactNode } from 'react'

import { ApiError } from '@/api/problem'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/Button'

export interface EmptyStateProps {
  title: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
  /** `search` bruges når en filtrering gav nul resultater. */
  variant?: 'default' | 'search'
  className?: string
}

/**
 * Tom tilstand. Skal altid forklare *hvorfor* der ikke er noget, og hvad
 * brugeren kan gøre – ikke bare konstatere "ingen data".
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  variant = 'default',
  className,
}: EmptyStateProps) {
  const defaultIcon = variant === 'search' ? <SearchX /> : <Inbox />

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <div
        className="flex size-12 items-center justify-center rounded-full bg-overlay-soft text-text-muted [&_svg]:size-6"
        aria-hidden="true"
      >
        {icon ?? defaultIcon}
      </div>

      <div className="max-w-sm">
        <p className="text-sm font-medium text-text-primary">{title}</p>
        {description && <p className="mt-1 text-[13px] text-text-muted">{description}</p>}
      </div>

      {action}
    </div>
  )
}

export interface ErrorStateProps {
  error: unknown
  onRetry?: () => void
  /** Vises i stedet for fejlens egen titel. */
  title?: string
  className?: string
}

/**
 * Fejltilstand. Skelner mellem manglende adgang og tekniske fejl, fordi
 * brugeren kan gøre noget ved det ene og ikke ved det andet.
 */
export function ErrorState({ error, onRetry, title, className }: ErrorStateProps) {
  const isForbidden = error instanceof ApiError && error.isForbidden
  const message =
    error instanceof ApiError
      ? error.userMessage
      : error instanceof Error
        ? error.message
        : 'Der opstod en uventet fejl.'

  const traceId = error instanceof ApiError ? error.problem.traceId : undefined

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'flex size-12 items-center justify-center rounded-full [&_svg]:size-6',
          isForbidden ? 'bg-warning-500/12 text-warning-400' : 'bg-danger-500/12 text-danger-400',
        )}
        aria-hidden="true"
      >
        {isForbidden ? <ShieldAlert /> : <AlertTriangle />}
      </div>

      <div className="max-w-md">
        <p className="text-sm font-medium text-text-primary">
          {title ?? (isForbidden ? 'Du har ikke adgang' : 'Indholdet kunne ikke hentes')}
        </p>
        <p className="mt-1 text-[13px] text-text-muted">{message}</p>
        {traceId && (
          <p className="mt-2 font-mono text-[11px] text-text-muted">
            Trace-id: <span className="select-all">{traceId}</span>
          </p>
        )}
      </div>

      {/* Der er ingen grund til at tilbyde "prøv igen" på en 403 – svaret bliver det samme. */}
      {onRetry && !isForbidden && (
        <Button variant="secondary" size="sm" onClick={onRetry} leadingIcon={<RefreshCw />}>
          Prøv igen
        </Button>
      )}
    </div>
  )
}
